import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'
import Utils from '@/utils/utils'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '@glass-designer/types'
import { SegmentTypes, AlignmentTypes, AlignmentTypeLookup } from '@/store/mathTypes'
import { OrderItemEdgeworkTypes, OrderEditorEdgeworkSettingTypes, UOMTypes, StockFaceTypeLookup, StockFaceTypes, InventoryStockTypes } from '@/store/types'


/**
 * SVG Helper
 * Generates the SVG output for a GlassShape object
 * SVGs are used for previewing the panel in order reports & production preview
 */
export default class SVGHelper {
  constructor(editor = null) {
    this.editor = editor
    this.svgStyles = Config.SVGStyles
    this.glassPanel = null
    this.viewBox = {}
    
    this.viewTransform = {
      scale: new THREE.Vector2(1, 1),
      translate: new THREE.Vector2(0, 0)
    }
    this.storedTransforms = []

    this.settings = {
      leftPadding: 15,
      rightPadding: 15,
      topPadding: 15,
      bottomPadding: 15,
      edgeworkSpacing: 10,
      legendWidth: 200,
      legendPadding: 10,
      dimensionSpacing: 10,
      holeDimensionSpacing: 15,
      dimMinDisplayScreenSize: 12,
      overallDimensionSpacing: 30,
      borderPadding: 2,
      sideViewWidth: 350,
      sideViewMargin: 5,
      sideViewPadding: 25,
    }
  }


  /**
   * Set the padding settings
   * 
   * @param {Number} top - Padding for the top side
   * @param {Number} right - Padding for the right side
   * @param {Number} bottom - Padding for the bottom side
   * @param {Number} left - Padding for the left side
   */
  setPadding(top, right, bottom, left) {
    this.settings.topPadding = top
    this.settings.rightPadding = right
    this.settings.bottomPadding = bottom
    this.settings.leftPadding = left
  }


  /**
   * Generate an SVG for a rectangle shape not created by the order editor
   * Used for simple rectangle line-entry items
   * 
   * @param {Object} rectangleData - Object containing details about the rectangle. Properties include:
   *    width - Width of the rectangle (in mm)
   *    height - Height of the rectangle (in mm)
   *    edgeworkDetailsList - Array of objects containing details about edgework. Properties for each object include:
   *       type - Type flag for the part of the rectangle that the edgework applies to
   *       name - Name of the edgework
   *       colour - Colour of the edgework to display
   *    childOrderItems - List of OrderItem objects associated with the order item the SVG is generated for
   * @param {Object} options - Options for generating the SVG. Options include:
   *    showDimensions - True/False to include dimensions in the SVG
   *    showEdgeworkLegend - True/False to include the edgework legend in the SVG (if edgeworks set)
   *    showStampLegend - True/False to include the stamp legend in the SVG (if stamp added)
   *    showSquareCorners - True/False to include square corner markers
   *    showOverallDimensions - True/False to include overall dimensions in the SVG
   *    showEdgeworkHighlights - True/False to include edgework highlights on the segments
   *    showPanelItems - True/False to include panel items
   *    showAnchors - True/False to include hole anchor points
   *    showBorder - True/False to include border around the SVG
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    centerDiagram - If set, centers the shape in the SVG view box
   *    preserveAspectRatio - Aspect ratio SVG setting
   *    orderStampConfig - Object containing order stamp configuration details from the server
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  generateRectangleSVG(rectangleData, options) {
    // Convert to segments
    var segmentList = []

    let applyRectangleEdgework = (segment, requiredEdgeType) => {
      segment.hasEdgework = false

      let edgeworkInfo = (rectangleData.edgeworkDetailsList || []).find(edgework => {
        return edgework.type == requiredEdgeType
      })

      if(edgeworkInfo) {
        segment.hasEdgework = true
        segment.edgeworkName = edgeworkInfo.name
        segment.edgeworkColour = edgeworkInfo.colour
      }
    }

    let width = Math.round(rectangleData.width)
    let height = Math.round(rectangleData.height)

    if(width <= 0 || height <= 0)
      return ''

    segmentList.push({
      type: GlassDesignerTypes.SegmentTypes.line,
      startPosition: new THREE.Vector2(0, 0),
      endPosition: new THREE.Vector2(width, 0),
      startCornerRadius: 0,
      endCornerRadius: 0,
      length: width
    })
    segmentList.push({
      type: GlassDesignerTypes.SegmentTypes.line,
      startPosition: new THREE.Vector2(width, 0),
      endPosition: new THREE.Vector2(width, height),
      startCornerRadius: 0,
      endCornerRadius: 0,
      length: height
    })
    segmentList.push({
      type: GlassDesignerTypes.SegmentTypes.line,
      startPosition: new THREE.Vector2(width, height),
      endPosition: new THREE.Vector2(0, height),
      startCornerRadius: 0,
      endCornerRadius: 0,
      length: width
    })
    segmentList.push({
      type: GlassDesignerTypes.SegmentTypes.line,
      startPosition: new THREE.Vector2(0, height),
      endPosition: new THREE.Vector2(0, 0),
      startCornerRadius: 0,
      endCornerRadius: 0,
      length: height
    })

    // Edgeworks
    var isPanelLongHorizontal = width >= height
    applyRectangleEdgework(segmentList[0], isPanelLongHorizontal ? OrderItemEdgeworkTypes.longA : OrderItemEdgeworkTypes.shortA)
    applyRectangleEdgework(segmentList[1], isPanelLongHorizontal ? OrderItemEdgeworkTypes.shortB : OrderItemEdgeworkTypes.longB)
    applyRectangleEdgework(segmentList[2], isPanelLongHorizontal ? OrderItemEdgeworkTypes.longB : OrderItemEdgeworkTypes.shortB)
    applyRectangleEdgework(segmentList[3], isPanelLongHorizontal ? OrderItemEdgeworkTypes.shortA : OrderItemEdgeworkTypes.longA)


    // Calculate extra info
    segmentList.forEach(segmentInfo => {
      segmentInfo.dimDir = segmentInfo.endPosition.clone().sub(segmentInfo.startPosition)
      segmentInfo.dimDir.normalize()
      segmentInfo.dimOutDir = new THREE.Vector2(segmentInfo.dimDir.y, -segmentInfo.dimDir.x)
      segmentInfo.midpoint = segmentInfo.endPosition.clone().add(segmentInfo.startPosition).multiplyScalar(0.5)
    })

    var panelItemList = []

    // Apply a stamp if setup in the order panel preview config
    if(options.orderStampConfig) {
      if(options.orderStampConfig.stampImageData) {

        // Check if the item has the required stock item
        var applyStampItem = false
        if(_.isArray(rectangleData.childOrderItems)) {
          if(rectangleData.childOrderItems.find(childOrderItem => childOrderItem.stockId == options.orderStampConfig.stampRequiredStockId) != null) {
            applyStampItem = true
          }
        }
        
        if(applyStampItem) {
          // Scale the stamp to be relative to the panel size for easiest viewing
          var panelResize = MathsUtils.aspectFitResize(width, height, options.scaleMaxWidth, options.scaleMaxHeight)
          var panelStampScale = (width / panelResize.width) * 30.0

          var resize = MathsUtils.aspectFitResize(options.orderStampConfig.stampImageWidth, options.orderStampConfig.stampImageHeight, panelStampScale, panelStampScale)
          var stampLeft = 0
          var stampTop = 0

          switch(options.orderStampConfig.stampAlignHorz) {
            case AlignmentTypes.center:
              stampLeft = Math.round(width/2 - resize.width/2)
              break
            case AlignmentTypes.right:
              stampLeft = Math.round(width - resize.width)
              break
          }
          switch(options.orderStampConfig.stampAlignVert) {
            case AlignmentTypes.middle:
              stampTop = Math.round(height/2 - resize.height/2)
              break
            case AlignmentTypes.bottom:
              stampTop = Math.round(height - resize.height)
              break
          }

          var stampBounds = new THREE.Box2(new THREE.Vector2(stampLeft, stampTop), new THREE.Vector2(stampLeft + resize.width, stampTop + resize.height))

          panelItemList.push({
            isOrderStampItem: true,
            bounds: stampBounds,
            imageSrc: options.orderStampConfig.stampImageData
          })
        }

      }
    }

    // Additional info
    var displayInfo = {
      overallWidth: width,
      overallHeight: height
    }

    return this._generateShapeSVG(segmentList, [], panelItemList, [], displayInfo, options)
  }


  /**
   * Generate an SVG for an order editor generated shape
   * 
   * @param {GlassShape} glassShape - GlassShape instance
   * @param {Object} options - Options for generating the SVG. Options include:
   *    showDimensions - True/False to include dimensions in the SVG
   *    showEdgeworkLegend - True/False to include the edgework legend in the SVG (if edgeworks set)
   *    showStampLegend - True/False to include the stamp legend in the SVG (if stamp added)
   *    showSquareCorners - True/False to include square corner markers
   *    showOverallDimensions - True/False to include overall dimensions in the SVG
   *    showEdgeworkHighlights - True/False to include edgework highlights on the segments
   *    showPanelItems - True/False to include panel items
   *    showRunoutLegend - True/False to include runouts in the legend
   *    showAnchors - True/False to include hole anchor points
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    centerDiagram - If set, centers the shape in the SVG view box
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  generateGlassShapeSVG(glassShape, options) {
    // Requires editor set
    if(!this.editor)
      return ''
    let edgeworkEdgeInfo = glassShape.calculateEdgeworkEdgeList()
    let glassCutBounds = glassShape.calculateGlassCutBounds()

    // Shape segments
    var segmentInfoList = glassShape.segmentList.map(segment => {
      var outputSegment = this._convertSegment(segment)

      if(segment.isEdgeworkAvailable && segment.orderEditorEdgeworkConfigId) {
        let edgeworkInfo = edgeworkEdgeInfo.segmentList.find(edge => edge.segmentId == segment.id)
        if(edgeworkInfo) {
          outputSegment.hasEdgework = true
          outputSegment.edgeworkName = this._generateEdgeworkServiceName(edgeworkInfo.name, edgeworkInfo.settingType, edgeworkInfo.edgeworkValue)
          outputSegment.edgeworkColour = edgeworkInfo.highlightColour
          outputSegment.edgeworkValue = edgeworkInfo.edgeworkValue
          outputSegment.edgeworkSettingType = edgeworkInfo.settingType
        }
      }

      return outputSegment
    })

    // Holes
    let outputHoleList = glassShape.getCenterCutoutList().map(centerCutout => {
      var outputHole = {
        segmentList: centerCutout.segmentList.map(segment => this._convertSegment(segment)),
        bounds: centerCutout.calculateSegmentBounds(),
        anchorWorldPoint: centerCutout.calculateAnchorWorldPoint(),
        dimensionLines: []
      }

      // Convert datum lines to just start/end positions
      outputHole.dimensionLines = _.compact(centerCutout.datumLinks.map(datumLink => {
        var dirToHole = datumLink.calculateLinkDirection(this.editor)
        if(dirToHole == null)
          return null

        var holeEdge = centerCutout.calculateAnchorWorldPoint()
        var datumOrigin = holeEdge.clone().addScaledVector(dirToHole, -Math.abs(datumLink.sourceOffsetDistance * datumLink.sourceOffsetDirection))

        return {
          startPosition: datumOrigin,
          endPosition: holeEdge
        }
      }))

      return outputHole
    })

    // Panel Items
    let outputPanelList = _.compact(glassShape.getPanelItemList().map(panelItem => {

      var texture = null

      // Note: Assumes the texture has been loaded when displaying the panel image via the editor
      if(panelItem.isOrderStampItem) {
        // Handle stamped special item
        texture = this.editor.loadOrderStampTextureSync()
        
      } else {        
        texture = this.editor.loadEditorLibraryItemTextureSync(panelItem.orderEditorLibraryItemId)
      }

      if(!texture)
        return null
      if(!texture.image)
        return null

      var outputItem = {
        isOrderStampItem: panelItem.isOrderStampItem,
        isEditorItem: true,
        bounds: panelItem.calculateBounds(),
        imageSrc: texture.image.src
      }

      return outputItem
    }))

    // Runouts
    var additionalSummaryList = []
    if(options.showRunoutLegend) {
      let shapeRunouts = glassShape.calculateRunoutList()

      if(shapeRunouts.length > 0) {
        additionalSummaryList.push({
          title: 'Runouts',
          noteList: shapeRunouts.map(runout => runout.description)
        })
      }
    }

    // Additional info
    var displayInfo = {
      overallLeft: glassCutBounds.min.x,
      overallTop: glassCutBounds.min.y,
      overallWidth: Math.round(glassCutBounds.max.x - glassCutBounds.min.x),
      overallHeight: Math.round(glassCutBounds.max.y - glassCutBounds.min.y)
    }

    return this._generateShapeSVG(segmentInfoList, outputHoleList, outputPanelList, additionalSummaryList, displayInfo, options)
  }


  /**
   * Generates the glass corner data SVG for an editor generated panel
   * SVG is used to display square corner settings & stamp position on the large label
   * 
   * @param {GlassShape} glassShape - GlassShape instance
   * @param {Object} options - Options for generating the SVG. Options include:
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  generateGlassCornerDataSVG(glassShape, options) {
    // Requires editor set
    if(!this.editor)
      return ''

    var cornerList = glassShape.calculateCornerData()

    return this._generateCornerDataSVG(cornerList, options)
  }


  /**
   * Generates the glass corner data SVG for a standard rectangle glass line item
   * SVG is used to display square corner settings & stamp position on the large label
   * 
   * @param {Object} rectangleData - Object containing details about the rectangle. Properties include:
   *    width - Width of the rectangle (in mm)
   *    height - Height of the rectangle (in mm)
   *    childOrderItems - List of OrderItem objects associated with the order item the SVG is generated for
   * @param {Object} options - Options for generating the SVG. Options include:
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    orderStampConfig - Object containing order stamp configuration details from the server
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  generateRectangleCornerDataSVG(rectangleData, options) {
    // The 4 corners
    // TL, TR, BR, BL
    var cornerList = [
      { name: 'TopLeft', isSquare: true, isStamp: false, point: new THREE.Vector2(0, 0) },
      { name: 'TopRight', isSquare: true, isStamp: false, point: new THREE.Vector2(rectangleData.width, 0) },
      { name: 'BottomRight', isSquare: true, isStamp: false, point: new THREE.Vector2(rectangleData.width, rectangleData.height) },
      { name: 'BottomLeft', isSquare: true, isStamp: false, point: new THREE.Vector2(0, rectangleData.height) },
    ]

    // Figure out where stamp is placed (if at all)
    if(options.orderStampConfig) {
      if(options.orderStampConfig.stampImageData) {

        // Check if the item has the required stock item
        var applyStampItem = false
        if(_.isArray(rectangleData.childOrderItems)) {
          if(rectangleData.childOrderItems.find(childOrderItem => childOrderItem.stockId == options.orderStampConfig.stampRequiredStockId) != null) {
            applyStampItem = true
          }
        }
        
        if(applyStampItem) {
          var stampCornerIndex = 0
          var isStampLeft = options.orderStampConfig.stampAlignHorz != AlignmentTypes.right
          var isStampTop = options.orderStampConfig.stampAlignVert != AlignmentTypes.bottom

          if(!isStampLeft && isStampTop)
            stampCornerIndex = 1;
          else if(!isStampLeft && !isStampTop)
            stampCornerIndex = 2;
          else if(isStampLeft && !isStampTop)
            stampCornerIndex = 3;

          cornerList[stampCornerIndex].isStamp = true
        }
      }
    }

    return this._generateCornerDataSVG(cornerList, options)
  }


  /**
   * Generate the glass corner data SVG from a corner list object
   * Called internally via generateRectangleCornerDataSVG() or generateGlassCornerDataSVG()
   * 
   * @param {Array<Object>} cornerList - Array of objects describing the 4 corners
   * @param {Object} options - Options for generating the SVG. Options include:
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  _generateCornerDataSVG(cornerList, options) {
    let scaleMaxWidth = options.scaleMaxWidth || 0
    let scaleMaxHeight = options.scaleMaxHeight || 0
    var viewScale = new THREE.Vector2(1, 1)

    var viewBoxPadding = 2
    this.viewBox = {
      x: -viewBoxPadding,
      y: -viewBoxPadding,
      width: scaleMaxWidth + viewBoxPadding*2,
      height: scaleMaxHeight + viewBoxPadding*2
    }
    this._setViewTransform(viewScale, new THREE.Vector2(0, 0))

    var svgContent = this._generateSVGHeader(options)

    var displayWidth = scaleMaxWidth
    var displayHeight = scaleMaxHeight

    var angleDisplayWidth = displayWidth * 0.25
    var angleDisplayHeight = displayHeight * 0.25

    var cornerOptions = { strokeColour: Config.SVGStyles.glassEdgeColour, strokeWidth: Config.SVGStyles.glassEdgeWidth, classList: ['svg-panel-corner-data'] }
    var cornerSquareOptions = { strokeColour: Config.SVGStyles.glassEdgeColour, strokeWidth: Config.SVGStyles.glassEdgeWidth, classList: ['svg-panel-corner-data-square'] }
    var cornerStampTextStyles = 'color: #000000; font-size: 60px; font-weight: bold;'
    var cornerStampClass = 'svg-panel-corner-data-stamp'

    svgContent += this._generateRectangle({ left: 0, top: 0, width: displayWidth, height: displayHeight }, cornerOptions)

    cornerList.forEach((corner, index) => {
      if(corner.isSquare) {
        switch(index) {
          case 0:
            svgContent += this._generateRectangle({ left: 0, top: 0, width: angleDisplayWidth, height: angleDisplayHeight }, cornerSquareOptions)
            break
          case 1:
            svgContent += this._generateRectangle({ left: displayWidth - angleDisplayWidth, top: 0, width: angleDisplayWidth, height: angleDisplayHeight }, cornerSquareOptions)
            break
          case 2:
            svgContent += this._generateRectangle({ left: displayWidth - angleDisplayWidth, top: displayHeight - angleDisplayHeight, width: angleDisplayWidth, height: angleDisplayHeight }, cornerSquareOptions)
            break
          case 3:
            svgContent += this._generateRectangle({ left: 0, top: displayHeight - angleDisplayHeight, width: angleDisplayWidth, height: angleDisplayHeight }, cornerSquareOptions)
            break
        }
      }

      // Display S for stamp
      if(corner.isStamp) {
        var stampPos = new THREE.Vector2(angleDisplayWidth/2, angleDisplayHeight/2)
        switch(index) {
          case 0:
            stampPos.set(angleDisplayWidth/2, angleDisplayHeight/2)
            break
          case 1:
            stampPos.set(displayWidth - angleDisplayWidth/2, angleDisplayHeight/2)
            break
          case 2:
            stampPos.set(displayWidth - angleDisplayWidth/2, displayHeight - angleDisplayHeight/2)
            break
          case 3:
            stampPos.set(angleDisplayWidth/2, displayHeight - angleDisplayHeight/2)
            break
        }

        svgContent += `<text dominant-baseline="central" class="${cornerStampClass}" text-anchor="middle" x="0" y="0" transform="translate(${Math.round(stampPos.x)},${Math.round(stampPos.y)})" style="${cornerStampTextStyles};">S</text>`
      }
    })

    svgContent += `</svg>`    

    return svgContent
  }


  /**
   * Generate an SVG for a list of segments and holes of a glass shape
   * Called internally
   * 
   * @param {Array<Object>} segmentInfoList - Array of objects containing info about the segment
   * @param {Array<Object>} holeList - Array of objects containing info about the holes / center cutouts in the shape
   * @param {Array<Object>} panelItemList - Array of objects containing info about the panel items in the shape
   * @param {Array<Object>} additionalSummaryList - Array of objects containing additional summary items to add to the summary notes
   * @param {Object} displayInfo - Object containing addition info to display. Properties include:
   *    overallLeft - Left side of the overall object bounds. May be different from segment bounds (eg, charged area)
   *    overallTop - Top side of the overall object bounds. May be different from segment bounds (eg, charged area)
   *    overallWidth - Width of the overall object to display. May be different from segment bounds (eg, charged area)
   *    overallHeight - Height of the overall object to display. May be different from segment bounds (eg, charged area)
   * @param {Object} options - Options for generating the SVG. Options include:
   *    showDimensions - True/False to include dimensions in the SVG
   *    showEdgeworkLegend - True/False to include the edgework legend in the SVG (if edgeworks set)
   *    showStampLegend - True/False to include the stamp legend in the SVG (if stamp added)
   *    showSquareCorners - True/False to include square corner markers
   *    showOverallDimensions - True/False to include overall dimensions in the SVG
   *    showEdgeworkHighlights - True/False to include edgework highlights on the segments
   *    showPanelItems - True/False to include panel items
   *    showAnchors - True/False to include hole anchor points
   *    showBorder - True/False to include border around the SVG
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    centerDiagram - If set, centers the shape in the SVG view box
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  _generateShapeSVG(segmentList, holeList, panelItemList, additionalSummaryList, displayInfo, options) {
    var width = Math.round(displayInfo.overallWidth)
    var height = Math.round(displayInfo.overallHeight)

    let scaleMaxWidth = options.scaleMaxWidth || 0
    let scaleMaxHeight = options.scaleMaxHeight || 0

    var bounds = new THREE.Box2()
    segmentList.forEach(segment => {
      bounds.expandByPoint(segment.startPosition)
      bounds.expandByPoint(segment.endPosition)
      bounds.expandByPoint(segment.midpoint)
    })

    // Adjust bounds for cutting bounds & display stuff
    if(options.showOverallDimensions) {
      width += this.settings.overallDimensionSpacing * 4
      height += this.settings.overallDimensionSpacing * 6
    }

    // Scale down the shape to fit into the maximum area
    // We dont scale down spacing / text / dimensions so they can be displayed 1:1 and are readable regardless of shape scaling
    var viewScale = new THREE.Vector2(1, 1)
    var viewTranslate = new THREE.Vector2(-bounds.min.x, -bounds.min.y)

    var legendPadding = 0
    if(options.showEdgeworkLegend)
      legendPadding = this.settings.legendWidth + this.settings.legendPadding

    let dstWidth = scaleMaxWidth
    let dstHeight = scaleMaxHeight

    this.viewBox = {
      x: -this.settings.leftPadding,
      y: -this.settings.topPadding,
      width: dstWidth + (this.settings.leftPadding + this.settings.rightPadding + legendPadding),
      height: dstHeight + (this.settings.topPadding + this.settings.bottomPadding)
    }

    // Note: We don't scale up if the shape is too small. Can do if required in the future
    if((width > scaleMaxWidth && scaleMaxWidth > 0) || (height > scaleMaxHeight && scaleMaxHeight > 0)) {

      var resize = MathsUtils.aspectFitResize(width, height, dstWidth, dstHeight)
      if(resize.width < width || resize.height < height) {
        viewScale.x = (resize.width / width)
        viewScale.y = (resize.height / height)

        // Center the shape
        if(options.centerDiagram) {
          viewTranslate.set(viewTranslate.x + (dstWidth/2 - resize.width/2) / viewScale.x, viewTranslate.y + (dstHeight/2 - resize.height/2) / viewScale.y)

          // Shift the viewbox
          if(width < height) {
            this.viewBox.x = dstWidth/2 - resize.width/2 - this.settings.leftPadding
            this.viewBox.width = resize.width + (this.settings.leftPadding + this.settings.rightPadding + legendPadding)

          } else {
            this.viewBox.y = dstHeight/2 - resize.height/2 - this.settings.topPadding
            this.viewBox.height = resize.height + (this.settings.topPadding + this.settings.bottomPadding)
          }

        }
      }
    }

    this._setViewTransform(viewScale, viewTranslate)

    // Generate the SVG content
    var svgContent = this._generateSVGHeader(options)


    // Segments path filled
    var pathCommands = this._generatePathCommandsForSegments(segmentList)

    // Hole paths in the shape path
    // Add to the same path list to get cutouts
    holeList.forEach(hole => {
      pathCommands = pathCommands.concat(this._generatePathCommandsForSegments(hole.segmentList))
    })
    svgContent += this._generatePathObject(pathCommands, {
      fillColour: Config.SVGStyles.glassPieceColour,
      strokeColour: Config.SVGStyles.glassEdgeColour,
      strokeWidth: Config.SVGStyles.glassEdgeWidth,
      fillRule: 'evenodd',
      classList: ['svg-panel-shape']
    })

    // Panel Items
    if(options.showPanelItems) {
      panelItemList.forEach(panelItem => {
        svgContent += this._generateImageObject({
          left: panelItem.bounds.min.x,
          top: panelItem.bounds.min.y,
          width: panelItem.bounds.max.x - panelItem.bounds.min.x,
          height: panelItem.bounds.max.y - panelItem.bounds.min.y,
          imageSrc: panelItem.imageSrc,
          classList: ['svg-panel-item']
        })
      })
    }


    // Segment edgeworks
    segmentList.forEach(segment => {
      if(segment.hasEdgework && options.showEdgeworkHighlights) {
        if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
          svgContent += this._generateLine(
            segment.startPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
            segment.endPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
            {
              strokeColour: segment.edgeworkColour,
              strokeWidth: Config.SVGStyles.edgeworkWidth,
              classList: ['svg-panel-edgework-flag']
            }
          )
        }
        if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
          svgContent += this._generateArc(
            segment.startPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
            segment.endPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
            segment.arcRadius,
            MathsUtils.degreesToRadians(segment.arcStartAngle),
            MathsUtils.degreesToRadians(segment.arcEndAngle),
            !segment.arcClockwise,
            {
              strokeColour: segment.edgeworkColour,
              strokeWidth: Config.SVGStyles.edgeworkWidth,
              classList: ['svg-panel-edgework-flag']
            }
          )
        }

      }
    })


    // Segment dimensions    
    if(options.showDimensions) {
      segmentList.forEach(segment => {
        // Only lines support for dimensions
        if(segment.type != GlassDesignerTypes.SegmentTypes.line)
          return

        svgContent += this._generateDimension({
          startPosition: segment.startPosition,
          endPosition: segment.endPosition,
          direction: segment.dimDir,
          outsetDirection: segment.dimOutDir,
          outsetSpacing: this.settings.dimensionSpacing,
          textBaseline: 'middle',
          hideLine: true,
          classList: ['svg-dimension-segment']
        })
      })
    }

    // Segment angles
    segmentList.forEach((segment, segmentIndex) => {
      let nextSegment = segmentList[segmentIndex == segmentList.length-1 ? 0 : segmentIndex+1]
      let angleDisplayPrecision = 2
      let minSegmentLength = 75
      let squareSize = 10 / this.viewTransform.scale.x
      let smallSquareSize = 4 / this.viewTransform.scale.x


      // Only display angles between 2 straight lines
      if(segment.type == SegmentTypes.line && nextSegment.type == SegmentTypes.line && options.showSquareCorners) {
        let primaryDirection = segment.dimDir
        let angleDirection = nextSegment.dimDir

        let primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(primaryDirection)))
        let angleDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(angleDirection)))

        let angle = MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(primaryDirDegrees, angleDirDegrees), angleDisplayPrecision)

        // Don't show anything if 0 or 180 degrees
        if(MathsUtils.roundNumber(Math.abs(angle - 180.0), angleDisplayPrecision) == 0 || MathsUtils.roundNumber(Math.abs(angle), angleDisplayPrecision) == 0)
          return

        // Show small version if the segments are too small
        let displaySize = squareSize
        if(segment.length < minSegmentLength || nextSegment.length < minSegmentLength)
          displaySize = smallSquareSize

        let isSquareAngle = MathsUtils.roundNumber(Math.abs(angle - 90), angleDisplayPrecision) == 0
        if(isSquareAngle) {
          let sqDisplayPoint = segment.endPosition
          let sqStartPnt = new THREE.Vector3(sqDisplayPoint.x - primaryDirection.x * displaySize, sqDisplayPoint.y - primaryDirection.y * displaySize)
          let sqMidPoint = sqStartPnt.clone().addScaledVector(angleDirection, displaySize)
          let sqEndPoint = new THREE.Vector3(sqDisplayPoint.x + angleDirection.x * displaySize, sqDisplayPoint.y + angleDirection.y * displaySize)

          let pathCommands = this._generatePointsPathCommands([
            { x: sqStartPnt.x, y: sqStartPnt.y },
            { x: sqMidPoint.x, y: sqMidPoint.y },
            { x: sqEndPoint.x, y: sqEndPoint.y }
          ])
          svgContent += this._generatePathObject(pathCommands, {
            strokeColour: Config.SVGStyles.dimensionColour,
            strokeWidth: 1,
            classList: _.concat(['svg-dimension-square'], options.classList || [])
          })
        }
      }

    })

    // Hole info
    holeList.forEach(hole => {
      // Hole dimensions
      if(options.showDimensions) {
        hole.dimensionLines.forEach(dimLine => {
          let dimDir = new THREE.Vector2(dimLine.endPosition.x - dimLine.startPosition.x, dimLine.endPosition.y - dimLine.startPosition.y)
          dimDir.normalize()

          svgContent += this._generateDimension({
            startPosition: dimLine.startPosition,
            endPosition: dimLine.endPosition,
            direction: dimDir,
            outsetDirection: new THREE.Vector2(dimDir.y, -dimDir.x),
            outsetSpacing: this.settings.holeDimensionSpacing,
            textBaseline: 'middle',
            textGap: 10,
            classList: ['svg-dimension-hole']
          })
        })
      }

      // Hole anchor point
      if(options.showAnchors) {
        let size = Math.min(hole.bounds.max.x - hole.bounds.min.x, hole.bounds.max.y - hole.bounds.min.y) * 0.6
        svgContent += this._generatePointCrosshair(hole.anchorWorldPoint, size, {
          strokeColour: Config.SVGStyles.holeAnchorColour,
          strokeWidth: Config.SVGStyles.holeAnchorWidth,
          classList: ['svg-panel-hole-anchor']
        })
      }
    })


    // Overall dimensions
    if(options.showOverallDimensions) {

      // Horizontal
      svgContent += this._generateDimension({
        startPosition: new THREE.Vector2(displayInfo.overallLeft, displayInfo.overallTop + displayInfo.overallHeight),
        endPosition: new THREE.Vector2(displayInfo.overallLeft + displayInfo.overallWidth, displayInfo.overallTop + displayInfo.overallHeight),
        direction: new THREE.Vector2(1, 0),
        outsetDirection: new THREE.Vector2(0, 1),
        outsetSpacing: this.settings.overallDimensionSpacing,
        textBaseline: 'bottom',
        textGap: 4,
        classList: ['svg-dimension-overall']
      })

      // Vertical
      svgContent += this._generateDimension({
        startPosition: new THREE.Vector2(displayInfo.overallLeft + displayInfo.overallWidth, displayInfo.overallTop),
        endPosition: new THREE.Vector2(displayInfo.overallLeft + displayInfo.overallWidth, displayInfo.overallTop + displayInfo.overallHeight),
        direction: new THREE.Vector2(0, 1),
        outsetDirection: new THREE.Vector2(1, 0),
        outsetSpacing: this.settings.overallDimensionSpacing,
        textBaseline: 'bottom',
        textGap: 4,
        classList: ['svg-dimension-overall']
      })
    }

    if(options.showBorder) {
      svgContent += this._generateBorder()
    }

    var summaryLegendList = []

    // Edgework legend
    if(options.showEdgeworkLegend) {
      // Collate edgework
      var edgeworkList = []
      segmentList.forEach(segment => {
        if(segment.hasEdgework) {
          if(!edgeworkList.find(curEdge => curEdge.name == segment.edgeworkName)) {
            edgeworkList.push({
              title: segment.edgeworkName,
              colour: segment.edgeworkColour
            })
          }
        }
      })

      if(edgeworkList.length > 0) {
        summaryLegendList.push({ title: 'Edgework', legendList: edgeworkList })
      }
    }

    // Stamp info
    if(options.showStampLegend) {
      var stampPanelItem = panelItemList.find(panelItem => panelItem.isOrderStampItem)
      if(stampPanelItem) {
        let stampNotes = ['Stamp Required']

        summaryLegendList.push({ title: 'Stamp', noteList: stampNotes })
      }
    }

    if(additionalSummaryList.length > 0) {
      summaryLegendList = _.concat(summaryLegendList, additionalSummaryList)
    }

    if(summaryLegendList.length > 0) {
      svgContent += this._generateSummaryLegendBox(summaryLegendList)
    }


    svgContent += `</svg>`

    return svgContent
  }


  /**
   * Generate an SVG for a simple layered assembly for a standard rectangle shape not created by the order editor
   * Used for simple rectangle assembly line-entry items
   * 
   * @param {Object} assemblyData - Object containing details about the assembly. Properties include:
   *    width - Overall width of the assembly (in mm)
   *    height - Overall geight of the assembly (in mm)
   *    childOrderItems - List of OrderItem objects associated with the order item the SVG is generated for.
   *       Must include stock with thickness dimensions
   *    layerList - List of objects containing the layers & stock & thicknesses to display
   * @param {Object} options - Options for generating the SVG. Options include:
   *    showDimensions - True/False to include dimensions in the SVG
   *    showSquareCorners - True/False to include square corner markers
   *    showOverallDimensions - True/False to include overall dimensions in the SVG
   *    showAnchors - True/False to include hole anchor points
   *    showSideView - True/False to include sideview
   *    showBorder - True/False to include border around the SVG
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  generateRectangleAssemblySVG(assemblyData, options) {
    let width = Math.round(assemblyData.width)
    let height = Math.round(assemblyData.height)

    if(width <= 0 || height <= 0)
      return ''

    var shapeOffset = 0
    var shapePadding = 150
    var maxWidth = 0
    var maxHeight = 0

    // Show a shape for each glass layer
    var displayShapeList = _.compact(assemblyData.layerList.map(layerInfo => {
      if(layerInfo.stockType != InventoryStockTypes.glass)
        return null

      let layerWidth = Math.round(layerInfo.glassWidth)
      let layerHeight = Math.round(layerInfo.glassHeight)

      // Convert to segments
      var segmentList = []

      segmentList.push({
        type: GlassDesignerTypes.SegmentTypes.line,
        startPosition: new THREE.Vector2(shapeOffset, 0),
        endPosition: new THREE.Vector2(shapeOffset + layerWidth, 0),
        startCornerRadius: 0,
        endCornerRadius: 0,
        length: layerWidth
      })
      segmentList.push({
        type: GlassDesignerTypes.SegmentTypes.line,
        startPosition: new THREE.Vector2(shapeOffset + layerWidth, 0),
        endPosition: new THREE.Vector2(shapeOffset + layerWidth, layerHeight),
        startCornerRadius: 0,
        endCornerRadius: 0,
        length: layerHeight
      })
      segmentList.push({
        type: GlassDesignerTypes.SegmentTypes.line,
        startPosition: new THREE.Vector2(shapeOffset + layerWidth, layerHeight),
        endPosition: new THREE.Vector2(shapeOffset, layerHeight),
        startCornerRadius: 0,
        endCornerRadius: 0,
        length: layerWidth
      })
      segmentList.push({
        type: GlassDesignerTypes.SegmentTypes.line,
        startPosition: new THREE.Vector2(shapeOffset, layerHeight),
        endPosition: new THREE.Vector2(shapeOffset, 0),
        startCornerRadius: 0,
        endCornerRadius: 0,
        length: layerHeight
      })

      // Calculate extra info
      segmentList.forEach(segmentInfo => {
        segmentInfo.dimDir = segmentInfo.endPosition.clone().sub(segmentInfo.startPosition)
        segmentInfo.dimDir.normalize()
        segmentInfo.dimOutDir = new THREE.Vector2(segmentInfo.dimDir.y, -segmentInfo.dimDir.x)
        segmentInfo.midpoint = segmentInfo.endPosition.clone().add(segmentInfo.startPosition).multiplyScalar(0.5)
      })

      shapeOffset += layerWidth + shapePadding
      maxWidth = Math.max(maxWidth, shapeOffset)
      maxHeight = Math.max(maxHeight, layerHeight)

      return {
        segmentList: segmentList,
        holeList: [],
        panelItemList: []
      }
    }))

    // Additional info
    var displayInfo = {
      overallLeft: 0,
      overallTop: 0,
      overallWidth: maxWidth,
      overallHeight: maxHeight
    }

    return this._generateShapeAssemblySVG(displayShapeList, assemblyData.layerList, displayInfo, options)
  }


  /**
   * Generate an SVG for a AssemblyGroup shape from the order editor
   * 
   * @param {AssemblyGroup} assemblyGroup - AssemblyGroup instance of the assembly object in the order editor
   * @param {Object} options - Options for generating the SVG. Options include:
   *    showDimensions - True/False to include dimensions in the SVG
   *    showSquareCorners - True/False to include square corner markers
   *    showOverallDimensions - True/False to include overall dimensions in the SVG
   *    showAnchors - True/False to include hole anchor points
   *    showSideView - True/False to include sideview
   *    showBorder - True/False to include border around the SVG
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  generateAssemblyShapeSVG(assemblyGroup, options = {}) {   
    // Get all child items
    var glassShapeList = assemblyGroup.getChildGlassShapeList()
    var assemblyLayerList = assemblyGroup.generateLayerList()

    var displayShapeList = []

    var patternLookup = {}
    var glassIndex = 0
    var shapeOffset = 0
    var shapePadding = 150    // TODO: Calculate full bounds & scale padding based on that
    var totalBounds = new THREE.Box2()

    // Convert layer colours to patterns
    assemblyLayerList.forEach(layerInfo => {
      if(layerInfo.uomType == UOMTypes.metresq) {

        // If already used a pattern for a stock, reuse it
        if(patternLookup[layerInfo.stock.id]) {
          layerInfo.colour = patternLookup[layerInfo.stock.id]
        } else {
          layerInfo.colour = `url(#pattern${glassIndex + 1})`
          glassIndex = (glassIndex + 1) % 2
          patternLookup[layerInfo.stock.id] = layerInfo.colour
        }
      }
    })

    glassShapeList.forEach(glassShape => {
      // Offset segments across for each panel added
      let shapeBounds = glassShape.calculateSegmentBounds()

      let segOffset = new THREE.Vector2(-shapeBounds.min.x + shapeOffset, -shapeBounds.min.y)

      // Generate the segments for the glass shape
      let segmentList = glassShape.segmentList.map(segment => this._convertSegment(segment, segOffset))

      segmentList.forEach(segment => {

        totalBounds.expandByPoint(segment.startPosition)
        totalBounds.expandByPoint(segment.endPosition)
        totalBounds.expandByPoint(segment.midpoint)
      })

      // Holes
      let outputHoleList = glassShape.getCenterCutoutList().map(centerCutout => {
        var outputHole = {
          segmentList: centerCutout.segmentList.map(segment => this._convertSegment(segment, segOffset)),
          bounds: centerCutout.calculateSegmentBounds(),
          anchorWorldPoint: centerCutout.calculateAnchorWorldPoint().add(segOffset),
          dimensionLines: []
        }

        // Convert datum lines to just start/end positions
        outputHole.dimensionLines = _.compact(centerCutout.datumLinks.map(datumLink => {
          var dirToHole = datumLink.calculateLinkDirection(this.editor)
          if(dirToHole == null)
            return null

          var holeEdge = centerCutout.calculateAnchorWorldPoint()
          var datumOrigin = holeEdge.clone().addScaledVector(dirToHole, -Math.abs(datumLink.sourceOffsetDistance * datumLink.sourceOffsetDirection))

          return {
            startPosition: datumOrigin.add(segOffset),
            endPosition: holeEdge.add(segOffset)
          }
        }))

        return outputHole
      })

      // Panel Items
      let outputPanelList = _.compact(glassShape.getPanelItemList().map(panelItem => {

        let texture = null

        // Note: Assumes the texture has been loaded when displaying the panel image via the editor
        if(panelItem.isOrderStampItem) {
          // Handle stamped special item
          texture = this.editor.loadOrderStampTextureSync()
          
        } else {        
          texture = this.editor.loadEditorLibraryItemTextureSync(panelItem.orderEditorLibraryItemId)
        }

        if(!texture)
          return null
        if(!texture.image)
          return null

        let outputItem = {
          isOrderStampItem: panelItem.isOrderStampItem,
          isEditorItem: true,
          bounds: panelItem.calculateBounds(),
          imageSrc: texture.image.src
        }
        outputItem.bounds.min.add(segOffset)
        outputItem.bounds.max.add(segOffset)

        return outputItem
      }))

      let outputDisplayShape = {
        segmentList: segmentList,
        holeList: outputHoleList,
        panelItemList: outputPanelList
      }

      displayShapeList.push(outputDisplayShape)

      shapeOffset += (shapeBounds.max.x - shapeBounds.min.x) + shapePadding
    })

    // Additional info
    var displayInfo = {
      overallLeft: Math.round(totalBounds.min.x),
      overallTop: Math.round(totalBounds.min.y),
      overallWidth: Math.round(totalBounds.max.x - totalBounds.min.x),
      overallHeight: Math.round(totalBounds.max.y - totalBounds.min.y)
    }

    return this._generateShapeAssemblySVG(displayShapeList, assemblyLayerList, displayInfo, options)
  }


  /**
   * Generate an SVG for a list of segments and holes of an assembly
   * Called internally
   * 
   * @param {Array<Object>} displayShapeList - Array of objects containing info about the segments in the assembly. Each object has the following properties:
   *    segmentInfoList - Array of segment objects containing the segments in the shape
   *    holdList - Array of objects containing info about the holes / center cutouts in the shape
   *    panelItemList - Array of objects containing info about the panel items in the shape
   * @param {Array<Object>} assemblyLayerList - Array of objects containing info about the layers of the assembly
   * @param {Object} displayInfo - Object containing addition info to display. Properties include:
   *    overallLeft - Left side of the overall object bounds. May be different from segment bounds (eg, charged area)
   *    overallTop - Top side of the overall object bounds. May be different from segment bounds (eg, charged area)
   *    overallWidth - Width of the overall object to display. May be different from segment bounds (eg, charged area)
   *    overallHeight - Height of the overall object to display. May be different from segment bounds (eg, charged area)
   * @param {Object} options - Options for generating the SVG. Options include:
   *    showDimensions - True/False to include dimensions in the SVG
   *    showSquareCorners - True/False to include square corner markers
   *    showOverallDimensions - True/False to include overall dimensions in the SVG
   *    showPanelItems - True/False to include panel items
   *    showAnchors - True/False to include hole anchor points
   *    showSideView - True/False to include sideview
   *    showBorder - True/False to include border around the SVG
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    scaleMaxWidth - Scales the output for display to a max width. Preserves aspect ratio
   *    scaleMaxHeight - Scales the output for display to a max height. Preserves aspect ratio
   *    centerDiagram - If set, centers the shape in the SVG view box
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @return {String} Text of the SVG (in SVG's XML format)
   */
  _generateShapeAssemblySVG(displayShapeList, assemblyLayerList, displayInfo, options) {
    var width = Math.round(displayInfo.overallWidth)
    var height = Math.round(displayInfo.overallHeight)

    let scaleMaxWidth = options.scaleMaxWidth || 0
    let scaleMaxHeight = options.scaleMaxHeight || 0

    var bounds = new THREE.Box2()
    displayShapeList.forEach(displayShape => {
      displayShape.segmentList.forEach(segment => {
        bounds.expandByPoint(segment.startPosition)
        bounds.expandByPoint(segment.endPosition)
        bounds.expandByPoint(segment.midpoint)
      })
    })

    // Adjust bounds for cutting bounds & display stuff
    if(options.showOverallDimensions) {
      width += this.settings.overallDimensionSpacing * 4
      height += this.settings.overallDimensionSpacing * 6
    }

    // Scale down the shape to fit into the maximum area
    // We dont scale down spacing / text / dimensions so they can be displayed 1:1 and are readable regardless of shape scaling
    var viewScale = new THREE.Vector2(1, 1)
    var viewTranslate = new THREE.Vector2(-bounds.min.x, -bounds.min.y)

    var sideViewMargin = 0
    var legendBoxWidth = 0
    if(options.showSideView)
      sideViewMargin = this.settings.sideViewWidth + this.settings.sideViewMargin
    if(options.showLegend || options.showEdgeworkLegend)
      legendBoxWidth = this.settings.legendWidth

    let dstWidth = scaleMaxWidth
    let dstHeight = scaleMaxHeight

    this.viewBox = {
      x: -this.settings.leftPadding,
      y: -this.settings.topPadding,
      width: dstWidth + (this.settings.leftPadding + this.settings.rightPadding + sideViewMargin + legendBoxWidth),
      height: dstHeight + (this.settings.topPadding + this.settings.bottomPadding)
    }

    // Note: We don't scale up if the shape is too small. Can do if required in the future
    if((width > scaleMaxWidth && scaleMaxWidth > 0) || (height > scaleMaxHeight && scaleMaxHeight > 0)) {

      var resize = MathsUtils.aspectFitResize(width, height, dstWidth, dstHeight)
      if(resize.width < width || resize.height < height) {
        viewScale.x = (resize.width / width)
        viewScale.y = (resize.height / height)
      }
    }

    this._setViewTransform(viewScale, viewTranslate)

    // Generate the SVG content
    var svgContent = this._generateSVGHeader(options)

    // Segments path filled
    var pathCommands = []
    displayShapeList.forEach(displayShape => {
      pathCommands = pathCommands.concat(this._generatePathCommandsForSegments(displayShape.segmentList))
    })

    // Hole paths in the shape path
    // Add to the same path list to get cutouts
    displayShapeList.forEach(displayShape => {
      displayShape.holeList.forEach(hole => {
        pathCommands = pathCommands.concat(this._generatePathCommandsForSegments(hole.segmentList))
      })
    })
    svgContent += this._generatePathObject(pathCommands, {
      fillColour: Config.SVGStyles.glassPieceColour,
      strokeColour: Config.SVGStyles.glassEdgeColour,
      strokeWidth: Config.SVGStyles.glassEdgeWidth,
      fillRule: 'evenodd',
      classList: ['svg-panel-shape']
    })

    // Panel Items
    if(options.showPanelItems) {
      displayShapeList.forEach(displayShape => {
        displayShape.panelItemList.forEach(panelItem => {
          svgContent += this._generateImageObject({
            left: panelItem.bounds.min.x,
            top: panelItem.bounds.min.y,
            width: panelItem.bounds.max.x - panelItem.bounds.min.x,
            height: panelItem.bounds.max.y - panelItem.bounds.min.y,
            imageSrc: panelItem.imageSrc,
            classList: ['svg-panel-item']
          })
        })
      })
    }


    // Segment edgeworks
    displayShapeList.forEach(displayShape => {
      displayShape.segmentList.forEach(segment => {
        if(segment.hasEdgework && options.showEdgeworkHighlights) {
          if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
            svgContent += this._generateLine(
              segment.startPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
              segment.endPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
              {
                strokeColour: segment.edgeworkColour,
                strokeWidth: Config.SVGStyles.edgeworkWidth,
                classList: ['svg-panel-edgework-flag']
              }
            )
          }
          if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
            svgContent += this._generateArc(
              segment.startPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
              segment.endPosition.clone().addScaledVector(segment.dimOutDir, this.settings.edgeworkSpacing),
              segment.arcRadius,
              MathsUtils.degreesToRadians(segment.arcStartAngle),
              MathsUtils.degreesToRadians(segment.arcEndAngle),
              !segment.arcClockwise,
              {
                strokeColour: segment.edgeworkColour,
                strokeWidth: Config.SVGStyles.edgeworkWidth,
                classList: ['svg-panel-edgework-flag']
              }
            )
          }

        }
      })
    })


    // Segment dimensions    
    if(options.showDimensions) {
      displayShapeList.forEach(displayShape => {
        displayShape.segmentList.forEach(segment => {
          // Only lines support for dimensions
          if(segment.type != GlassDesignerTypes.SegmentTypes.line)
            return

          svgContent += this._generateDimension({
            startPosition: segment.startPosition,
            endPosition: segment.endPosition,
            direction: segment.dimDir,
            outsetDirection: segment.dimOutDir,
            outsetSpacing: this.settings.dimensionSpacing,
            textBaseline: 'middle',
            hideLine: true,
            classList: ['svg-dimension-segment']
          })
        })
      })
    }

    // Segment angles
    displayShapeList.forEach(displayShape => {
      displayShape.segmentList.forEach((segment, segmentIndex) => {
        let nextSegment = displayShape.segmentList[segmentIndex == displayShape.segmentList.length-1 ? 0 : segmentIndex+1]
        let angleDisplayPrecision = 2
        let minSegmentLength = 75
        let squareSize = 10 / this.viewTransform.scale.x
        let smallSquareSize = 4 / this.viewTransform.scale.x


        // Only display angles between 2 straight lines
        if(segment.type == SegmentTypes.line && nextSegment.type == SegmentTypes.line && options.showSquareCorners) {
          let primaryDirection = segment.dimDir
          let angleDirection = nextSegment.dimDir

          let primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(primaryDirection)))
          let angleDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(angleDirection)))

          let angle = MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(primaryDirDegrees, angleDirDegrees), angleDisplayPrecision)

          // Don't show anything if 0 or 180 degrees
          if(MathsUtils.roundNumber(Math.abs(angle - 180.0), angleDisplayPrecision) == 0 || MathsUtils.roundNumber(Math.abs(angle), angleDisplayPrecision) == 0)
            return

          // Show small version if the segments are too small
          let displaySize = squareSize
          if(segment.length < minSegmentLength || nextSegment.length < minSegmentLength)
            displaySize = smallSquareSize

          let isSquareAngle = MathsUtils.roundNumber(Math.abs(angle - 90), angleDisplayPrecision) == 0
          if(isSquareAngle) {
            let sqDisplayPoint = segment.endPosition
            let sqStartPnt = new THREE.Vector3(sqDisplayPoint.x - primaryDirection.x * displaySize, sqDisplayPoint.y - primaryDirection.y * displaySize)
            let sqMidPoint = sqStartPnt.clone().addScaledVector(angleDirection, displaySize)
            let sqEndPoint = new THREE.Vector3(sqDisplayPoint.x + angleDirection.x * displaySize, sqDisplayPoint.y + angleDirection.y * displaySize)

            let pathCommands = this._generatePointsPathCommands([
              { x: sqStartPnt.x, y: sqStartPnt.y },
              { x: sqMidPoint.x, y: sqMidPoint.y },
              { x: sqEndPoint.x, y: sqEndPoint.y }
            ])
            svgContent += this._generatePathObject(pathCommands, {
              strokeColour: Config.SVGStyles.dimensionColour,
              strokeWidth: 1,
              classList: _.concat(['svg-dimension-square'], options.classList || [])
            })
          }
        }
      })

      // Hole info
      displayShape.holeList.forEach(hole => {
        // Hole dimensions
        if(options.showDimensions) {
          hole.dimensionLines.forEach(dimLine => {
            let dimDir = new THREE.Vector2(dimLine.endPosition.x - dimLine.startPosition.x, dimLine.endPosition.y - dimLine.startPosition.y)
            dimDir.normalize()

            svgContent += this._generateDimension({
              startPosition: dimLine.startPosition,
              endPosition: dimLine.endPosition,
              direction: dimDir,
              outsetDirection: new THREE.Vector2(dimDir.y, -dimDir.x),
              outsetSpacing: this.settings.holeDimensionSpacing,
              textBaseline: 'middle',
              textGap: 10,
              classList: ['svg-dimension-hole']
            })
          })
        }

        // Hole anchor point
        if(options.showAnchors) {
          let size = Math.min(hole.bounds.max.x - hole.bounds.min.x, hole.bounds.max.y - hole.bounds.min.y) * 0.6
          svgContent += this._generatePointCrosshair(hole.anchorWorldPoint, size, {
            strokeColour: Config.SVGStyles.holeAnchorColour,
            strokeWidth: Config.SVGStyles.holeAnchorWidth,
            classList: ['svg-panel-hole-anchor']
          })
        }
      })
    })


    // Overall dimensions
    if(options.showOverallDimensions) {

      // Horizontal
      svgContent += this._generateDimension({
        startPosition: new THREE.Vector2(displayInfo.overallLeft, displayInfo.overallTop + displayInfo.overallHeight),
        endPosition: new THREE.Vector2(displayInfo.overallLeft + displayInfo.overallWidth, displayInfo.overallTop + displayInfo.overallHeight),
        direction: new THREE.Vector2(1, 0),
        outsetDirection: new THREE.Vector2(0, 1),
        outsetSpacing: this.settings.overallDimensionSpacing,
        textBaseline: 'bottom',
        textGap: 4,
        classList: ['svg-dimension-overall']
      })

      // Vertical
      svgContent += this._generateDimension({
        startPosition: new THREE.Vector2(displayInfo.overallLeft + displayInfo.overallWidth, displayInfo.overallTop),
        endPosition: new THREE.Vector2(displayInfo.overallLeft + displayInfo.overallWidth, displayInfo.overallTop + displayInfo.overallHeight),
        direction: new THREE.Vector2(0, 1),
        outsetDirection: new THREE.Vector2(1, 0),
        outsetSpacing: this.settings.overallDimensionSpacing,
        textBaseline: 'bottom',
        textGap: 4,
        classList: ['svg-dimension-overall']
      })
    }

    if(options.showBorder) {
      svgContent += this._generateBorder()
    }

    var legendBoxList = []
    if(assemblyLayerList.length > 0) {
      legendBoxList.push({
        title: 'Assembly Layers',
        legendList: assemblyLayerList.map(layerInfo => {
          return { title: layerInfo.name, colour: layerInfo.colour }
        })
      })

      // Show sides
      // Facing up is always pointing to the left (outside)
      var layerHasSides = assemblyLayerList.some(layer => layer.stock.isStockUsingFaces)
      if(layerHasSides) {
        var layerSideIndex = 0
        var sideList = []
        assemblyLayerList.forEach(layer => {
          if(layer.stock.isStockUsingFaces) {
            sideList.push({
              title: `${layerSideIndex + 1}`,
              value: Utils.getLookupName(StockFaceTypeLookup, layer.stockFaceSideUpType)
            })
            layerSideIndex++

            sideList.push({
              title: `${layerSideIndex + 1}`,
              value: Utils.getLookupName(StockFaceTypeLookup, layer.stockFaceSideUpType == StockFaceTypes.smooth ? StockFaceTypes.rough : StockFaceTypes.smooth)
            })
            layerSideIndex++
          }
        })

        legendBoxList.push({
          title: 'Sides',
          lookupList: sideList
        })
      }
    }

    svgContent += this._generateSummaryLegendBox(legendBoxList)

    // Side view of the assembly layers
    if(assemblyLayerList.length > 0) {
      svgContent += this._generateAssemblySideView(assemblyLayerList)
    }

    svgContent += `</svg>`

    return svgContent
  }


  /**
   * Generate a linear dimension
   * Internally called
   *
   * @param {Object} options - Contains all the details about the dimension to generate. Properties include:
   *    startPosition: Start position of the dimensions
   *    endPosition: Start position of the dimensions
   *    direction - Running direction of the dimension
   *    outsetDirection - Outwards direction to move out
   *    textBaseline - Baseline of the text
   *    outsetSpacing - Spacing from the line outwards
   *    textGap - Additional spacing outwards of the text
   *    hideLine - If true, hides the line/arrows & displays text only
   *    classList - If set, additional classes to add to the objects
   * @return {String} Outputs a string containing SVG elements
   */
  _generateDimension(options) {
    var dimensionColour = Config.SVGStyles.dimensionColour
    var dimensionFontSize = Config.SVGStyles.dimensionFontSize
    var arrowLength = this._convertScreenToWorldSpace(5)
    var arrowWidth = this._convertScreenToWorldSpace(3)
    var dimensionTextGap = options.textGap === undefined ? 2 : options.textGap
    var textBaseline = options.textBaseline || 'bottom'

    var outsetDist = this._convertScreenToWorldSpace(options.outsetSpacing)

    var startPos = new THREE.Vector2(options.startPosition.x, options.startPosition.y)
    var endPos = new THREE.Vector2(options.endPosition.x, options.endPosition.y)

    var distance = endPos.clone().sub(startPos).length()

    // Ignore showing dimension if smaller then the minimum
    if(distance * this.viewTransform.scale.x <= this.settings.dimMinDisplayScreenSize)
      return ''

    var direction = new THREE.Vector2(options.direction.x, options.direction.y)
    var outsetDirection = new THREE.Vector2(options.outsetDirection.x, options.outsetDirection.y)

    startPos.addScaledVector(outsetDirection, outsetDist)
    endPos.addScaledVector(outsetDirection, outsetDist)

    var textPos = endPos.clone().addScaledVector(direction, -distance * 0.5)
    textPos.addScaledVector(outsetDirection, -this._convertScreenToWorldSpace(dimensionTextGap))

    var pathCommands = this._generatePointsPathCommands([
      { x: startPos.x, y: startPos.y },
      { x: endPos.x, y: endPos.y },
    ])
    var paths = ''
    if(!options.hideLine) {
      paths += this._generatePathObject(pathCommands, {
        strokeColour: dimensionColour,
        strokeWidth: 1,
        classList: _.concat(['svg-dimension-line'], options.classList || [])
      })
    }

    let dimensionText = Math.round(distance) + 'mm'
    var textRotation = Math.round(THREE.Math.radToDeg(direction.angle())) + 270 % 360
    textRotation = textRotation % 180 + 90

    // Flip to make more readable
    if(textRotation >= 90)
      textRotation += 180

    var textClasses = _.concat(['svg-dimension-text'], options.classList || []).join(' ')
    let textStyles = `font-size: ${dimensionFontSize}; fill: #000000; paint-order: stroke; stroke-width: 2px; stroke: #FFFFFF;`
    paths += `<text dominant-baseline="${textBaseline}" class="${textClasses}" text-anchor="middle" x="0" y="0" transform="translate(${this._transformPointValueX(textPos.x)},${this._transformPointValueY(textPos.y)}) rotate(${textRotation})" style="${textStyles}">${dimensionText}</text>`

    // Arrow head
    var arrowStart = startPos.clone().addScaledVector(direction, -1)
    var arrowPos1 = arrowStart.clone().addScaledVector(direction, arrowLength).addScaledVector(outsetDirection, arrowWidth)
    var arrowPos2 = arrowPos1.clone().addScaledVector(outsetDirection, -arrowWidth*2)
    pathCommands = this._generatePointsPathCommands([
      { x: arrowStart.x, y: arrowStart.y },
      { x: arrowPos1.x, y: arrowPos1.y },
      { x: arrowPos2.x, y: arrowPos2.y },
      { x: arrowStart.x, y: arrowStart.y }
    ])
    if(!options.hideLine) {
      paths += this._generatePathObject(pathCommands, {
        fillColour: dimensionColour,
        classList: _.concat(['svg-dimension-arrow'], options.classList || [])
      })
    }

    arrowStart = endPos.clone().addScaledVector(direction, 1)
    arrowPos1 = arrowStart.clone().addScaledVector(direction, -arrowLength).addScaledVector(outsetDirection, arrowWidth)
    arrowPos2 = arrowPos1.clone().addScaledVector(outsetDirection, -arrowWidth*2)
    pathCommands = this._generatePointsPathCommands([
      { x: arrowStart.x, y: arrowStart.y },
      { x: arrowPos1.x, y: arrowPos1.y },
      { x: arrowPos2.x, y: arrowPos2.y },
      { x: arrowStart.x, y: arrowStart.y }
    ])
    if(!options.hideLine) {
      paths += this._generatePathObject(pathCommands, {
        fillColour: dimensionColour,
        classList: _.concat(['svg-dimension-arrow'], options.classList || [])
      })
    }

    return paths
  }


  /**
   * Generate the path commands for a list of segments to be used in a path SVG element
   * 
   * @param {Array<Object>} segmentInfoList - Array of objects containing info about the segment
   * @return {Array<String>} Returns an array of path commands in SVG path format
   */
  _generatePathCommandsForSegments(segmentInfoList) {
    var pathCommandList = []

    segmentInfoList.forEach((segment, segmentIndex) => {
      let prevSegment = segmentInfoList[segmentIndex == 0 ? segmentInfoList.length-1 : segmentIndex-1]
      let nextSegment = segmentInfoList[segmentIndex == segmentInfoList.length-1 ? 0 : segmentIndex+1]

      if(segmentIndex == 0) {
        if(segment.type == 'line') {
          if(segment.startCornerRadius == 0.0) {
            pathCommandList.push('M' + this._transformPointValueX(segment.startPosition.x) + ' ' + this._transformPointValueY(segment.startPosition.y))
          } else {
            // Adjust for the corner radius
            let roundCornerData = MathsUtils.calculateCornerRounding(
              prevSegment.startPosition,
              prevSegment.endPosition,
              segment.startPosition,
              segment.endPosition,
              segment.startCornerRadius,
              false
            )

            if(!roundCornerData) {
              pathCommandList.push('M' + this._transformPointValueX(segment.startPosition.x) + ' ' + this._transformPointValueY(segment.startPosition.y))
            } else {
              pathCommandList.push('M' + this._transformPointValueX(roundCornerData.segment2Start.x) + ' ' + this._transformPointValueY(roundCornerData.segment2Start.y))
            }
          }
        }
      }

      if(segment.type == 'line') {
        if(segment.endCornerRadius == 0.0)
          pathCommandList.push('L' + this._transformPointValueX(segment.endPosition.x) + ' ' + this._transformPointValueY(segment.endPosition.y))
        else {
          // Adjust for the corner radius
          let roundCornerData = MathsUtils.calculateCornerRounding(
            segment.startPosition,
            segment.endPosition,
            nextSegment.startPosition,
            nextSegment.endPosition,
            segment.endCornerRadius,
            false
          )

          if(!roundCornerData) {
            pathCommandList.push('L' + this._transformPointValueX(segment.endPosition.x) + ' ' + this._transformPointValueY(segment.endPosition.y))
          } else {
            pathCommandList.push('L' + this._transformPointValueX(roundCornerData.segment1End.x) + ' ' + this._transformPointValueY(roundCornerData.segment1End.y))

            let arcPathCommands = this._generateSVGArcPath(
              roundCornerData.segment1End,
              roundCornerData.segment2Start,
              roundCornerData.radius,
              roundCornerData.startAngle,
              roundCornerData.endAngle,
              roundCornerData.clockwise
            )
            pathCommandList.push(arcPathCommands.join(' '))
          }
        }

      } else if(segment.type == 'arc') {
        if(segmentIndex == 0)
          pathCommandList.push('M' + this._transformPointValueX(segment.startPosition.x) + ' ' + this._transformPointValueY(segment.startPosition.y))

        let arcPathCommands = this._generateSVGArcPath(
          segment.startPosition,
          segment.endPosition,
          segment.arcRadius,
          MathsUtils.degreesToRadians(segment.arcStartAngle),
          MathsUtils.degreesToRadians(segment.arcEndAngle),
          !segment.arcClockwise
        )
        pathCommandList.push(arcPathCommands.join(' '))
      }
    })

    return pathCommandList
  }


  /**
   * Generate the total summary / legend box
   * 
   * @param {Array<Object>} summaryList - Array of objects for each summary category in the legend. For each object:
   *    - title - Title of the summary
   *    - legendList - Array of objects containing 'title' and 'colour' fields for legend items
   *    - noteList - Array of strings to display as a list of dot point notes
   *    - lookupList - Array of objects containing 'title' and 'value' fields for a grid of values
   */
  _generateSummaryLegendBox(summaryList) {
    if(summaryList.length == 0)
      return ''

    let legendTitleFontSize = Config.SVGStyles.legendTitleFontSize
    let legendFontSize = Config.SVGStyles.legendFontSize

    var svgContent = ''

    var boxWidth = this.settings.legendWidth
    var boxHeight = this.viewBox.height - this.settings.borderPadding * 2
    var boxLeft = this.viewBox.width - boxWidth - this.settings.borderPadding + this.viewBox.x
    var boxTop = this.viewBox.y + this.settings.borderPadding

    var options = {
      strokeColour: Config.SVGStyles.legendBorderColour,
      strokeWidth: 1,
      classList: ['svg-summary-box']
    }

    // Convert to SVG coords
    this._saveViewTransform()
    this._setViewTransform(new THREE.Vector2(1,1), new THREE.Vector2(0,0))

    svgContent += this._generateLine(new THREE.Vector2(boxLeft, boxTop + boxHeight), new THREE.Vector2(boxLeft, boxTop), options)

    var titlePosX = Math.round(boxLeft + boxWidth / 2)
    var currentPosY = boxTop + 10
    var headerPadding = 15
    var lineHeight = 18
    var footerPadding = 10

    summaryList.forEach(summaryItem => {
      svgContent += this._generateRectangle({ left: boxLeft, top: currentPosY - 10, width: boxWidth, height: 14 }, {
        strokeColour: Config.SVGStyles.legendBorderColour,
        strokeWidth: 1,
        fillColour: Config.SVGStyles.headerFillColour,
        classList: ['svg-summary-header-box']
      })
      svgContent += `<text dominant-baseline="baseline" class="svg-summary-header-title" text-anchor="middle" x="0" y="0" transform="translate(${titlePosX},${currentPosY})" style="font-size: ${legendTitleFontSize};">${summaryItem.title}</text>`
      currentPosY += 4 + headerPadding;

      if(_.isArray(summaryItem.legendList)) {
        let textPosX = boxLeft + 30

        summaryItem.legendList.forEach(dataItem => {
          // Word wrap for text too long. Basic word wrap for SVG which doesn't support word wrap well
          let textList = Utils.calculateTextWordWrap(dataItem.title, 30);

          let outputText = dataItem.title
          if(textList.length > 1) {
            outputText = textList.map((line, index) => {
              return `<tspan x="0" dy="${index * 12}px">${line}</tspan>`
            })
          }

          svgContent += `<text dominant-baseline="middle" class="svg-summary-legend-text" text-anchor="left" x="0" y="0" transform="translate(${textPosX},${currentPosY})" style="font-size: ${legendFontSize};">${outputText}</text>`

          svgContent += this._generateRectangle({
            left: boxLeft + 4,
            top: currentPosY - 8,
            width: 16,
            height: 14
          }, {
            fillColour: dataItem.colour,
            strokeColour: Config.SVGStyles.legendBorderColour,
            strokeWidth: 1,
            classList: ['svg-summary-legend-colour']
          })

          currentPosY += textList.length * lineHeight
        })
      }

      if(_.isArray(summaryItem.noteList)) {
        let textPosX = boxLeft + 20

        summaryItem.noteList.forEach(note => {
          // Word wrap for text too long. Basic word wrap for SVG which doesn't support word wrap well
          let textList = Utils.calculateTextWordWrap(note, 30);

          let outputText = note
          if(textList.length > 1) {
            outputText = textList.map((line, index) => {
              return `<tspan x="0" dy="${index * 12}px">${line}</tspan>`
            })
          }

          svgContent += `<text dominant-baseline="middle" class="svg-summary-note-text" text-anchor="left" x="0" y="0" transform="translate(${textPosX},${currentPosY})" style="font-size: ${legendFontSize};">${outputText}</text>`

          let dotRadius = 2
          svgContent += this._generateCircle({
            centerX: boxLeft + 10 + dotRadius/2,
            centerY: currentPosY - 3 + dotRadius/2,
            radius: dotRadius
          }, {
            fillColour: Config.SVGStyles.summaryDotColour,
            classList: ['svg-summary-note-dot']
          })

          currentPosY += textList.length * lineHeight
        })
      }

      if(_.isArray(summaryItem.lookupList)) {
        let titleWidth = 40
        let valueWidth = boxWidth - titleWidth
        let lookupHeight = 16

        summaryItem.lookupList.forEach(dataItem => {
          svgContent += this._generateRectangle({
            left: boxLeft,
            top: currentPosY - headerPadding,
            width: titleWidth,
            height: lookupHeight
          }, {
            strokeColour: Config.SVGStyles.legendBorderColour,
            strokeWidth: 1,
            classList: ['svg-summary-lookup-grid']
          })
          svgContent += this._generateRectangle({
            left: boxLeft + titleWidth,
            top: currentPosY - headerPadding,
            width: valueWidth,
            height: lookupHeight
          }, {
            strokeColour: Config.SVGStyles.legendBorderColour,
            strokeWidth: 1,
            classList: ['svg-summary-lookup-grid']
          })

          let textPosX = boxLeft + 3
          let textPosY = currentPosY - headerPadding + 8;
          svgContent += `<text dominant-baseline="middle" class="svg-summary-lookup-key" text-anchor="left" x="0" y="0" transform="translate(${textPosX},${textPosY})" style="font-size: ${legendFontSize};">${dataItem.title}</text>`

          textPosX += titleWidth          
          svgContent += `<text dominant-baseline="middle" class="svg-summary-lookup-value" text-anchor="left" x="0" y="0" transform="translate(${textPosX},${textPosY})" style="font-size: ${legendFontSize};">${dataItem.value}</text>`

          currentPosY += lookupHeight
        })
      }
      
      currentPosY += footerPadding
    })

    this._restoreViewTransform()

    return svgContent
  }


  /**
   * Generate the assembly sideview
   */
  _generateAssemblySideView(assemblyLayerList) {
    var viewTitleFontSize = Config.SVGStyles.viewTitleFontSize
    var sideTitleFontSize = '11px'

    var boxWidth = this.settings.sideViewWidth
    var boxHeight = this.viewBox.height - this.settings.sideViewMargin * 2;
    var boxLeft = this.viewBox.width - boxWidth - this.settings.legendWidth - this.settings.legendPadding + this.viewBox.x
    var boxTop = this.viewBox.y + this.settings.sideViewMargin

    var options = {
      strokeColour: Config.SVGStyles.legendBorderColour,
      strokeWidth: 1,
      fillColour: '#808080',
      classList: ['svg-assembly-layer-box']
    }

    var svgContent = ''

    // Convert to SVG coords
    this._saveViewTransform()
    this._setViewTransform(new THREE.Vector2(1,1), new THREE.Vector2(0,0))

    svgContent += this._generateLine(new THREE.Vector2(boxLeft, this.viewBox.y), new THREE.Vector2(boxLeft, this.viewBox.y + this.viewBox.height), {
      strokeColour: Config.SVGStyles.legendBorderColour,
      strokeWidth: 1,
      classList: ['svg-assembly-layer-line']
    })

    var top = boxTop + this.settings.sideViewPadding
    var layerHeight = boxHeight - this.settings.sideViewPadding * 2
    var layerWidth = 15;
    var layerSpacing = 1;
    var lengthLayerHeight = 15

    var displayLayerCount = assemblyLayerList.filter(layerInfo => {
      if(layerInfo.thickness <= 0)
        return false
      return true
    }).length

    var displayTotalWidth = displayLayerCount * (layerWidth + layerSpacing)

    var left = Math.round(boxLeft + boxWidth/2 - displayTotalWidth/2)

    var layerLabelIndex = 0
    assemblyLayerList.forEach(layerInfo => {

      // Don't display in the side view if the thickness is zero
      if(layerInfo.thickness <= 0)
        return

      options.fillColour = layerInfo.colour

      if(layerInfo.uomType == UOMTypes.metresq) {
        svgContent += this._generateRectangle({
          left: left,
          top: top,
          width: layerWidth,
          height: layerHeight        
        }, options)

        // Show legend numbers for layer sides (if glass layer is two sided)
        if(layerInfo.stock && layerInfo.stock.isStockUsingFaces) {
          svgContent += this._generateLabel({
            left: left,
            top: top + layerHeight + 14,
            text: `${layerLabelIndex + 1}`,
            fontSize: 11
          })
          svgContent += this._generateLabel({
            left: left + layerWidth,
            top: top + layerHeight + 14,
            text: `${layerLabelIndex + 2}`,
            fontSize: 11
          })

          layerLabelIndex += 2;
        }
      }
      if(layerInfo.uomType == UOMTypes.length) {
        // Draw as border
        svgContent += this._generateRectangle({
          left: left,
          top: top,
          width: layerWidth,
          height: lengthLayerHeight        
        }, options)

        svgContent += this._generateRectangle({
          left: left,
          top: top + layerHeight - lengthLayerHeight,
          width: layerWidth,
          height: lengthLayerHeight        
        }, options)
      }

      left += layerWidth + layerSpacing;
    })

    var titlePosX = Math.round(boxLeft + boxWidth / 2)
    var titlePosY = boxTop + 10
    svgContent += `<text dominant-baseline="baseline" class="svg-edgework-legend-title" text-anchor="middle" x="0" y="0" transform="translate(${titlePosX},${titlePosY})" style="font-size: ${viewTitleFontSize};">Side View</text>`

    // Show Outside / Inside
    var sideTitlePosX = Math.round(boxLeft + this.settings.sideViewPadding)
    var sideTitlePosY = boxTop + 60
    svgContent += `<text dominant-baseline="baseline" class="svg-edgework-legend-title" text-anchor="start" x="0" y="0" transform="translate(${sideTitlePosX},${sideTitlePosY})" style="font-size: ${sideTitleFontSize};">Outside</text>`

    sideTitlePosX = Math.round(boxLeft + boxWidth - this.settings.sideViewPadding)
    svgContent += `<text dominant-baseline="baseline" class="svg-edgework-legend-title" text-anchor="end" x="0" y="0" transform="translate(${sideTitlePosX},${sideTitlePosY})" style="font-size: ${sideTitleFontSize};">Inside</text>`


    this._restoreViewTransform()

    return svgContent
  }


  /**
   * Generate the border commands
   */
  _generateBorder() {
    // Convert to SVG coords
    this._saveViewTransform()
    this._setViewTransform(new THREE.Vector2(1,1), new THREE.Vector2(0,0))

    var svgContent = this._generateRectangle({
      left: this.viewBox.x + this.settings.borderPadding,
      top: this.viewBox.y + this.settings.borderPadding,
      width: this.viewBox.width - this.settings.borderPadding * 2,
      height: this.viewBox.height - this.settings.borderPadding * 2,
    }, {
      strokeWidth: 1,
      strokeColour: '#000000'
    })

    this._restoreViewTransform()

    return svgContent
  }


  /**
   * Generate the path commands for a list of points
   */
  _generatePointsPathCommands(pathPoints) {
    if(!_.isArray(pathPoints) || pathPoints.length == 0)
      return []

    return pathPoints.map((point, index) => {
      var cmd = index == 0 ? 'M' : 'L'

      return `${cmd}` + this._transformPointValueX(point.x) + ' ' + this._transformPointValueY(point.y)
    })
  }


  /**
   * Generate a path SVG object using details & path command list
   */
  _generatePathObject(pathCommands, pathOptions) {
    let cmds = pathCommands.join(' ')
    
    var pathStyles = {
      stroke: pathOptions.strokeColour || 'none',
      fill: pathOptions.fillColour || 'none',
      'stroke-width': (pathOptions.strokeWidth || 0) + 'px',
      'fill-rule': pathOptions.fillRule || 'nonzero'
    }

    var styleData = Object.keys(pathStyles).map(key => {
      return `${key}: ${pathStyles[key]}`
    }).join('; ')

    var classList = (pathOptions.classList || []).join(' ')

    var pathContent = `<path style="${styleData}" class="${classList}" d="${cmds}" />`

    return pathContent
  }


  /**
   * Generate the edgework service name with value & setting type
   * Used for service item in order & diagram
   */
  _generateEdgeworkServiceName(edgeworkName, settingType, value) {
    let edgeworkPrecision = 2
    let edgeworkDisplayValue = Number.parseFloat(MathsUtils.roundNumber(value, edgeworkPrecision)).toFixed(edgeworkPrecision)

    var serviceName = edgeworkName
    if(settingType == OrderEditorEdgeworkSettingTypes.angle)
      serviceName += ' ' + edgeworkDisplayValue + ' DEG'
    else if(settingType == OrderEditorEdgeworkSettingTypes.size)
      serviceName += ' ' + edgeworkDisplayValue + ' MM'      

    return serviceName
  }


  /**
   * Generate the SVG header for the file
   * 
   * @param {Object} options - Options for generating the SVG. Options include:
   *    svgWidth - width attribute in the SVG element
   *    svgHeight - height attribute in the SVG element
   *    preserveAspectRatio - Aspect ratio SVG setting
   * @returns {String} Returns SVG text
   */
  _generateSVGHeader(options) {
    var preserveAspectRatio = options.preserveAspectRatio || 'xMidYMid meet'
    var svgContent = `<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" preserveAspectRatio="${preserveAspectRatio}" `
    svgContent += `viewBox="${Math.round(this.viewBox.x)} ${Math.round(this.viewBox.y)} ${Math.round(this.viewBox.width)} ${Math.round(this.viewBox.height)}" `
    svgContent += 'y="0" x="0" '
    if(options.svgWidth)
      svgContent += `width="${options.svgWidth}" `
    if(options.svgHeight)
      svgContent += `height="${options.svgHeight}" `
    svgContent += `>`

    // Pattern definitions
    svgContent += '<defs>'

    svgContent += `<pattern id="pattern1" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">`
    svgContent += `   <path d="M3 0 3 8" style="stroke:#808080; stroke-width:1" />`
    svgContent += `</pattern>`
    svgContent += `<pattern id="pattern2" width="8" height="8" patternTransform="rotate(90)" patternUnits="userSpaceOnUse">`
    svgContent += `   <path d="M3 0 3 8" style="stroke:#808080; stroke-width:1" />`
    svgContent += `</pattern>`
    svgContent += `<pattern id="pattern3" width="8" height="8" patternTransform="rotate(135)" patternUnits="userSpaceOnUse">`
    svgContent += `   <path d="M3 0 3 8" style="stroke:#808080; stroke-width:1" />`
    svgContent += `</pattern>`

    svgContent += '</defs>'

    return svgContent
  }


  /**
   * Generate an ARC path command list based on ellipse/arc details
   * Note: Assumes the current position is the start of the arc
   * Internally called
   */
  _generateSVGArcPath(startPosition, endPosition, radius, startAngleRads, endAngleRads, clockwise) {
    var pathCommands = []

    // Angle for start -> end
    let rotation = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(Math.atan2(endPosition.y - startPosition.y, endPosition.x - startPosition.x)))

    let angleDiff = MathsUtils.calculateAngleDistanceDegrees(360 - MathsUtils.radiansToDegrees(startAngleRads), 360 - MathsUtils.radiansToDegrees(endAngleRads), clockwise)
    let isLargeArc = Math.abs(angleDiff) >= 180
    let isSweepFlag = !clockwise

    pathCommands.push('A', Math.round(radius * this.viewTransform.scale.x), Math.round(radius * this.viewTransform.scale.y))
    pathCommands.push(rotation.toFixed(1), isLargeArc ? '1' : '0', isSweepFlag ? '1' : '0')
    pathCommands.push(this._transformPointValueX(endPosition.x), this._transformPointValueY(endPosition.y))

    return pathCommands
  }


  /**
   * Generate a crosshair for a point
   */
  _generatePointCrosshair(position, radius, pathOptions) {
    let pathCommands = [
      `M` + this._transformPointValueX(position.x) + ' ' + this._transformPointValueY(position.y - radius),
      `L` + this._transformPointValueX(position.x) + ' ' + this._transformPointValueY(position.y + radius),
      `M` + this._transformPointValueX(position.x - radius) + ' ' + this._transformPointValueY(position.y),
      `L` + this._transformPointValueX(position.x + radius) + ' ' + this._transformPointValueY(position.y),
    ]

    return this._generatePathObject(pathCommands, pathOptions)
  }


  /**
   * Generate a line
   */
  _generateLine(startPosition, endPosition, pathOptions) {
    let pathCommands = [
      `M` + this._transformPointValueX(startPosition.x) + ' ' + this._transformPointValueY(startPosition.y),
      `L` + this._transformPointValueX(endPosition.x) + ' ' + this._transformPointValueY(endPosition.y),
    ]

    return this._generatePathObject(pathCommands, pathOptions)
  }


  /**
   * Generate an arc
   */
  _generateArc(startPosition, endPosition, radius, startAngleRads, endAngleRads, clockwise, pathOptions) {
    let pathCommands = [
      `M` + this._transformPointValueX(startPosition.x) + ' ' + this._transformPointValueY(startPosition.y),
    ]
    pathCommands = pathCommands.concat(this._generateSVGArcPath(startPosition, endPosition, radius, startAngleRads, endAngleRads, clockwise))

    return this._generatePathObject(pathCommands, pathOptions)
  }


  /**
   * Generate a rectangle
   */
  _generateRectangle(options, pathOptions) {
    let pathCommands = [
      `M` + this._transformPointValueX(options.left) + ' ' + this._transformPointValueY(options.top),
      `L` + this._transformPointValueX(options.left + options.width) + ' ' + this._transformPointValueY(options.top),
      `L` + this._transformPointValueX(options.left + options.width) + ' ' + this._transformPointValueY(options.top + options.height),
      `L` + this._transformPointValueX(options.left) + ' ' + this._transformPointValueY(options.top + options.height),
      `L` + this._transformPointValueX(options.left) + ' ' + this._transformPointValueY(options.top),
    ]

    return this._generatePathObject(pathCommands, pathOptions)
  }


  /**
   * Generate a circle
   */
  _generateCircle(options, pathOptions) {
    var classList = (options.classList || []).join(' ')

    var centerX = this._transformPointValueX(options.centerX)
    var centerY = this._transformPointValueY(options.centerY)
    var radius = this._transformSizeValueX(options.radius)

    var pathStyles = {
      stroke: pathOptions.strokeColour || 'none',
      fill: pathOptions.fillColour || 'none',
      'stroke-width': (pathOptions.strokeWidth || 0) + 'px',
      'fill-rule': pathOptions.fillRule || 'nonzero'
    }
    var styleData = Object.keys(pathStyles).map(key => {
      return `${key}: ${pathStyles[key]}`
    }).join('; ')

    var circleContent = `<circle styles="${styleData}" class="${classList}" cx="${centerX}" cy="${centerY}" r="${radius}" fill="${pathStyles.fill}" stroke="${pathStyles.stroke}" />`

    return circleContent
  }


  /**
   * Generate an image object
   */
   _generateImageObject(options) {
    var classList = (options.classList || []).join(' ')

    let x = this._transformPointValueX(options.left)
    let y = this._transformPointValueY(options.top)
    let width = this._transformSizeValueX(options.width)
    let height = this._transformSizeValueY(options.height)

    var imageContent = `<image class="${classList}" x="${x}" y="${y}" width="${width}" height="${height}" href="${options.imageSrc}" />`

    return imageContent
  }


  /**
   * Generate a label object
   */
  _generateLabel(options) {
    var classList = (options.classList || []).join(' ')

    var x = this._transformPointValueX(options.left)
    var y = this._transformPointValueY(options.top)
    var fontSize = options.fontSize || 12;
    var text = options.text || ''

    return `<text dominant-baseline="baseline" class="${classList}" text-anchor="middle" x="0" y="0" transform="translate(${x},${y})" style="font-size: ${fontSize}px;">${text}</text>`
  }


  /**
   * Set the view transform
   */
  _setViewTransform(scale, translate) {
    this.viewTransform.scale.set(scale.x, scale.y)
    this.viewTransform.translate.set(translate.x, translate.y)
  }


  /**
   * Save the current view transform to the stack
   * Paired with _restoreViewTransform()
   */
  _saveViewTransform() {
    this.storedTransforms.push({
      scale: this.viewTransform.scale.clone(),
      translate: this.viewTransform.translate.clone(),
    })
  }


  /**
   * Restore the last saved view transform
   */
  _restoreViewTransform() {
    let prev = this.storedTransforms.pop()
    if(prev) {
      this.viewTransform.scale.set(prev.scale.x, prev.scale.y)
      this.viewTransform.translate.set(prev.translate.x, prev.translate.y)
    }
  }


  /**
   * Convert a value from screen coordinates to world coordinates
   */
  _convertScreenToWorldSpace(coordinate) {
    // Assumes scale is same for x/y axis & aspect ratio retained
    return coordinate / this.viewTransform.scale.x
  }


  /**
   * Transform a point coordinate by the scale & adjust for sub-pixel rounding
   * Returns a string with point for pathing
   */
  _transformPointValueX(coordinate) {
    var halfEdge = '.5'     // Add half-edges to fix anti-aliasing
    return `${Math.round((coordinate + this.viewTransform.translate.x) * this.viewTransform.scale.x)}${halfEdge}`
  }

  /**
   * Transform a point coordinate by the scale & adjust for sub-pixel rounding
   * Returns a string with point for pathing
   */
  _transformPointValueY(coordinate) {
    var halfEdge = '.5'     // Add half-edges to fix anti-aliasing
    return `${Math.round((coordinate + this.viewTransform.translate.y) * this.viewTransform.scale.y)}${halfEdge}`
  }


  /**
   * Transform a size coordinate by the scale & adjust for sub-pixel rounding
   * Returns a string with point for pathing
   */
   _transformSizeValueX(coordinate) {
    var halfEdge = '.5'     // Add half-edges to fix anti-aliasing
    return `${Math.round(coordinate * this.viewTransform.scale.x)}${halfEdge}`
  }

  /**
   * Transform a size coordinate by the scale & adjust for sub-pixel rounding
   * Returns a string with point for pathing
   */
   _transformSizeValueY(coordinate) {
    var halfEdge = '.5'     // Add half-edges to fix anti-aliasing
    return `${Math.round(coordinate * this.viewTransform.scale.y)}${halfEdge}`
  }


  /**
   * Convert a GlassSegment into an object with a format for the SVG functions
   * Additional properties will be added to the segment object, such as edgework, etc
   * Adds offset if set for all positions
   */
  _convertSegment(segment, offset = null) {
    var outputSegment = {
      type: segment.type,
      startPosition: segment.startPosition.clone(),
      endPosition: segment.endPosition.clone(),
      midpoint: segment.getMidpoint(),
      dimDir: segment.getDirection(),
      dimOutDir: segment.getOutDirection(),
      startCornerRadius: segment.startCornerRadius,
      endCornerRadius: segment.endCornerRadius,
      length: segment.getLength(),
      hasEdgework: false
    }

    if(offset) {
      outputSegment.startPosition.x += offset.x
      outputSegment.startPosition.y += offset.y
      outputSegment.endPosition.x += offset.x
      outputSegment.endPosition.y += offset.y
      outputSegment.midpoint.x += offset.x
      outputSegment.midpoint.y += offset.y
    }

    if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
      outputSegment.arcCenterPosition = segment.arcCenterPosition.clone()
      outputSegment.arcRadius = segment.arcRadius
      outputSegment.arcStartAngle = segment.arcStartAngle
      outputSegment.arcEndAngle = segment.arcEndAngle
      outputSegment.arcClockwise = segment.arcClockwise

      if(offset) {
        outputSegment.arcCenterPosition.x += offset.x
        outputSegment.arcCenterPosition.y += offset.y
      }
    }

    return outputSegment
  }




}